// social-api — D39/D40 SocialBar 前端 hook
//
// 路由(server/internal/httpapi/social_handlers.go):
//   POST /gapi/user/likes/toggle       {target_type, target_id}
//   POST /gapi/user/dislikes/toggle    {target_type, target_id}
//   GET  /gapi/public/packs/{pack_id}/social-stats        (pack;公开)
//   GET  /gapi/public/articles/{article_id}/social-stats  (article;公开;2026-06-12 扩展 comment_count/tip_count)
//
// 2026-06-02 file 社交删除(spec §3):file_info.like/dislike 列已删,target 从
// {file, pack} retarget 为 {pack, article}。

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@dogdisk/shared'
import type { SocialBarStats } from '@dogdisk/shared'

export type SocialTarget = 'pack' | 'article'

/** article social-stats 扩展(2026-06-12;pack 无这两字段) */
export type ArticleSocialStats = SocialBarStats & {
  comment_count?: number
  tip_count?: number
}

export type ToggleResult = {
  new_state: 'like' | 'dislike' | 'none'
  like_count: number
  dislike_count: number
}

const statsKey = (target: SocialTarget, id: string) =>
  ['social', 'stats', target, id] as const

function statsPath(target: SocialTarget, id: string): string {
  if (target === 'article') {
    return `/gapi/public/articles/${encodeURIComponent(id)}/social-stats`
  }
  return `/gapi/public/packs/${encodeURIComponent(id)}/social-stats`
}

export function useSocialStats(target: SocialTarget, id: string | null) {
  return useQuery({
    queryKey: statsKey(target, id ?? ''),
    enabled: Boolean(id),
    queryFn: () => apiClient.get<SocialBarStats>(statsPath(target, id!)),
    staleTime: 30_000,
  })
}

export function useToggleLike(target: SocialTarget, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post<ToggleResult>('/gapi/user/likes/toggle', { target_type: target, target_id: id }),
    onSuccess: (res) => {
      qc.setQueryData<SocialBarStats>(statsKey(target, id), {
        like_count: res.like_count,
        dislike_count: res.dislike_count,
        i_liked: res.new_state === 'like',
        i_disliked: res.new_state === 'dislike',
      })
    },
  })
}

export function useToggleDislike(target: SocialTarget, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post<ToggleResult>('/gapi/user/dislikes/toggle', { target_type: target, target_id: id }),
    onSuccess: (res) => {
      qc.setQueryData<SocialBarStats>(statsKey(target, id), {
        like_count: res.like_count,
        dislike_count: res.dislike_count,
        i_liked: res.new_state === 'like',
        i_disliked: res.new_state === 'dislike',
      })
    },
  })
}

// useSaveFileToPan — 单文件 inline 转存到自己的网盘
//
// 2026-05-20 Phase 24:R-07 决议落地为 `POST /gapi/user/files/{file_id}/save-to-pan {target_parent_id?}`
// 独立 endpoint(不走 pack_id='__inline__' 设计)。server:pack/save_inline.go::SaveInlineToPan。
//
// 业务规则:
//   - VIP-only(普通用户拒,errcode=VIP_REQUIRED)
//   - 来源:owner OR file 所在 active pack 用户有访问权(free / 订阅 / integrator)
//   - 复制 file_info 不复制密钥(base_file 共享;sha256_original 唯一键)
//   - 同名冲突拒(NameDuplicate)
//
// 返回:{file_info_id, base_file_id}(供前端跳转 / 缓存)
export function useSaveFileToPan() {
  return useMutation({
    mutationFn: (fileId: string) =>
      apiClient.post<{ file_info_id: string; base_file_id: string }>(
        `/gapi/user/files/${fileId}/save-to-pan`,
        {},
      ),
  })
}
